import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

// Make Pusher available globally — required by laravel-echo internals
(window as unknown as { Pusher: typeof Pusher }).Pusher = Pusher;

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/api/v1/';

// Derive the broadcasting auth endpoint from the API base URL.
// The backend registers POST /broadcasting/auth via Broadcast::routes().
// It lives at the app root (not under /api/v1/) so we strip the path.
const origin = new URL(API_BASE).origin;
export const BROADCASTING_AUTH_ENDPOINT = `${origin}/broadcasting/auth`;

// Singleton Echo instance — reused across re-renders to avoid
// reconnecting on every mount/unmount cycle.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _echoInstance: Echo<any> | null = null;
let _echoToken: string | null = null;

/**
 * Returns a singleton Echo instance. Recreates it only if the token changes.
 * Returns null if VITE_PUSHER_APP_KEY is not configured — callers should
 * skip real-time features gracefully in that case.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createEchoInstance(token: string): Echo<any> | null {
  const pusherKey = import.meta.env.VITE_PUSHER_APP_KEY as string | undefined;
  if (!pusherKey) return null;

  if (_echoInstance && _echoToken === token) {
    return _echoInstance;
  }
  // Token changed or first call — disconnect old instance and create new one
  if (_echoInstance) {
    try { _echoInstance.disconnect(); } catch {}
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _echoInstance = new (Echo as any)({
    broadcaster: 'pusher',
    key: pusherKey,
    cluster: (import.meta.env.VITE_PUSHER_APP_CLUSTER as string) ?? 'mt1',
    forceTLS: true,
    authEndpoint: BROADCASTING_AUTH_ENDPOINT,
    auth: {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  });
  _echoToken = token;
  return _echoInstance!;
}
